// Accordion labels switched to sentence case (acronyms kept).
import React, { FC, useEffect, useMemo, useState } from 'react';

import { AccessSummaryTypes, NativeResources } from '@ariksa/inventory-core';
import { Accordion, Box, Center } from '@chakra-ui/react';
import { toLower } from 'lodash';
import filter from 'lodash/filter';
import { useDispatch, useSelector } from 'react-redux';

import {
  renderStackedCloudNameAndIcon,
  StackedCell,
  WithResourceIcon,
} from 'components/DataDisplay';
import { CustomTable2 as Table } from 'components/DataDisplay/NewTable/Table';
import { ActionButton } from 'components/DataEntry';
import { IconTypes, TrustedEntityIcon } from 'components/Icons';
import { MetaAccordionItem } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/Components/MetaAccordionItem';
import { useMetadataDrawerTabs } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/hooks/useMetadataDrawerTabs';
import {
  renderHighPrivilegesCell,
  renderList,
} from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/utils';
import { useActiveResourceActions } from 'containers/ActiveCloudResource/hooks/useActiveResourceActions';
import { useRedirectToGraph } from 'containers/ActiveCloudResource/hooks/useRedirectToGraph';
import { selectActiveResource } from 'containers/ActiveCloudResource/selectors';
import { actions } from 'containers/ActiveCloudResource/slice';
import { MetadataTabs } from 'containers/ActiveCloudResource/types';
import { useResourceType } from 'containers/App/hooks/useResourceType';

export const SnowflakeDatabaseIdentity: FC = () => {
  const dispatch = useDispatch();
  const { canAccessSummary, resourceAccountId } = useSelector(
    selectActiveResource,
  );
  const { linkedResourceDetails } = useSelector(selectActiveResource);
  const { accessByIdentity } = canAccessSummary;
  const {
    getCanAccessSummary,
    updateActiveResource,
  } = useActiveResourceActions();
  const { getTabIndex } = useMetadataDrawerTabs();
  const { getCloudNativeName, getCloudAgnosticName } = useResourceType();
  const { redirectToGraph } = useRedirectToGraph();
  const [loadingTrustedEntityUuid, setLoadingTrustedEntityUuid] = useState<
    string | null
  >(null);

  useEffect(() => {
    getCanAccessSummary(AccessSummaryTypes.AccessByIdentity);
  }, [getCanAccessSummary]);

  const users = useMemo(
    () =>
      filter(accessByIdentity.data, o => o?.source_agnostic_name === 'User'),
    [accessByIdentity.data],
  );
  const roles = useMemo(
    () =>
      filter(accessByIdentity.data, o => o?.source_agnostic_name === 'Role'),
    [accessByIdentity.data],
  );

  const name = {
    header: <Box pl="thLeftPaddingWithIcon">Name</Box>,
    accessor: '',
    render: ({ row }) => (
      <WithResourceIcon resourceType={row?.source_native_class}>
        <StackedCell
          upper={row?.source_name}
          lower={
            getCloudNativeName(row?.source_native_class) +
            ' / ' +
            getCloudAgnosticName(row?.source_native_class)
          }
        />
      </WithResourceIcon>
    ),
  };
  const owner = {
    header: 'Owner',
    accessor: 'permission_entity_owner',
    align: 'left',
  };

  const privileges = [
    {
      header: 'High Privileges',
      accessor: 'crud_level_counts_high',
      render: renderHighPrivilegesCell,
    },
    {
      header: '# of Privileges',
      accessor: 'total_permission_count',
    },
  ];

  const action = {
    header: 'Trusted Entities',
    accessor: '',
    align: 'left',
    render: ({ row }) => {
      const resourceType = row?.source_native_class as NativeResources;
      const tab =
        resourceType === NativeResources.SnowflakeRoles
          ? MetadataTabs.Permissions
          : MetadataTabs.Access;

      return (
        <Center onClick={e => e.stopPropagation()}>
          <ActionButton
            icon={<TrustedEntityIcon />}
            label="Trusted entities"
            isLoading={
              loadingTrustedEntityUuid === row?.source_uuid &&
              linkedResourceDetails.isLoading
            }
            onClick={() => {
              setLoadingTrustedEntityUuid(row?.source_uuid);
              dispatch(
                actions.getLinkedResourceDetails({
                  q: {
                    insightV2Request: {
                      resource_uuids: [row?.source_uuid],
                    },
                  },
                  onSuccess: res => {
                    const item = res?.data?.items?.[0];
                    if (!item) return;

                    const targetResourceType =
                      item?.native_name ?? resourceType;
                    const targetTab =
                      targetResourceType === NativeResources.SnowflakeRoles
                        ? MetadataTabs.Permissions
                        : MetadataTabs.Access;

                    updateActiveResource({
                      resourceType: targetResourceType,
                      uuid: item?.uuid ?? row?.source_uuid,
                      resourceId: item?.resource_id ?? row?.source_name,
                      accountId:
                        item?.account ??
                        row?.source_account_id ??
                        resourceAccountId,
                      cloudProvider: item?.provider ?? row?.source_provider,
                      tabIndex: getTabIndex(targetResourceType, targetTab),
                    });
                  },
                }),
              );
            }}
          />
        </Center>
      );
    },
  };

  const usersColumns = [
    name,
    {
      header: 'Identity Provider',
      accessor: 'source_provider',
      align: 'left',
      render: ({ value }) => renderStackedCloudNameAndIcon(toLower(value)),
    },
    {
      header: 'Granted Roles',
      accessor: 'source_granted_role_count',
      //align: 'left',
      //render: renderList,
    },
    ...privileges,
  ];
  const rolesColumns = [
    name,
    owner,
    {
      header: 'Type',
      accessor: 'permission_entity_managed_type',
      align: 'left',
    },
    {
      header: 'Granted On',
      accessor: 'permission_entity_granted_on',
      align: 'left',
      render: renderList,
    },
    ...privileges,
    action,
  ];

  return (
    <>
      <Accordion defaultIndex={[0]} allowMultiple>
        <MetaAccordionItem
          iconType={IconTypes.User}
          label="User identities that can access this resource"
          totalCount={users?.length}
          onClickDownload={() => {}}
          isLoading={accessByIdentity.isLoading}
          panel={
            <Table
              columns={usersColumns}
              data={users}
              isLoading={accessByIdentity.isLoading}
            />
          }
          iconBGColor="primary"
          onShowGraph={() =>
            redirectToGraph('identity', MetadataTabs.Principal)
          }
        />
        <MetaAccordionItem
          iconType={IconTypes.Role}
          label="Role identities that can access this resource"
          totalCount={roles?.length}
          onClickDownload={() => {}}
          isLoading={accessByIdentity.isLoading}
          panel={
            <Table
              columns={rolesColumns}
              data={roles}
              isLoading={accessByIdentity.isLoading}
            />
          }
          iconBGColor="primary"
        />
      </Accordion>
    </>
  );
};
